import { useEffect, useRef, useState, type FormEvent } from 'react';
import type { TenantSecuritySettings } from '@detect/shared';
import { useTenant } from '../../contexts/TenantContext';
import { tenantService } from '../../services/tenants';
import { LoadingSpinner, ErrorState } from '../../components/common';
import { passwordToggleBlocker, withProvider } from './securityPolicy';

/**
 * Sign-in policy (#9; mirrors Desk). Account admins choose which SSO providers the
 * organization allows and whether email/password sign-in is switched off. Enforcement is
 * server-side on every request. Detect has no native 2FA: it is whatever the identity
 * provider enforces.
 */
export function SecurityTab() {
  const { tenantId, hasRole } = useTenant();
  const canEdit = hasRole('account_admin');
  const [saved, setSaved] = useState<TenantSecuritySettings | null>(null);
  const [draft, setDraft] = useState<TenantSecuritySettings | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [saving, setSaving] = useState(false);
  /** Bumped on every (re)load of a tenant: a save that resolves after a switch, even one
   *  back to the same tenant, belongs to an older generation and must not land. */
  const generation = useRef(0);

  useEffect(() => {
    // Drop the previous tenant's policy first, even when there is no tenant now (fail closed).
    generation.current += 1;
    setSaved(null);
    setDraft(null);
    setError(null);
    setSaving(false);
    if (!tenantId) return;
    let live = true;
    tenantService.current(tenantId).then(
      (t) => {
        if (!live) return;
        setSaved(t.security);
        setDraft(t.security);
      },
      (err: unknown) => live && setError(err)
    );
    return () => {
      live = false;
    };
  }, [tenantId]);

  if (error !== null && draft === null) return <ErrorState error={error} />;
  if (!draft || !saved) return <LoadingSpinner />;

  const dirty = JSON.stringify(draft) !== JSON.stringify(saved);
  const blocker = passwordToggleBlocker(draft);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!tenantId || !canEdit) return;
    if (
      draft.localPasswordDisabled &&
      !saved.localPasswordDisabled &&
      !window.confirm(
        'Members will have to sign in with the enabled SSO provider(s). Account admins can still use email and password. Continue?'
      )
    )
      return;
    const gen = generation.current;
    setSaving(true);
    setError(null);
    try {
      const t = await tenantService.updateSecurity(tenantId, draft);
      if (generation.current !== gen) return;
      setSaved(t.security);
      setDraft(t.security);
    } catch (err) {
      if (generation.current === gen) setError(err);
    } finally {
      if (generation.current === gen) setSaving(false);
    }
  };

  return (
    <form onSubmit={submit}>
      {error !== null && <ErrorState error={error} />}
      <fieldset className="form-field" disabled={!canEdit}>
        <legend>Single sign-on providers</legend>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={draft.ssoProviders.google}
            onChange={(e) => setDraft(withProvider(draft, 'google', e.target.checked))}
          />
          Google
        </label>
        <label className="checkbox-label">
          <input
            type="checkbox"
            checked={draft.ssoProviders.microsoft}
            onChange={(e) => setDraft(withProvider(draft, 'microsoft', e.target.checked))}
          />
          Microsoft
        </label>
      </fieldset>
      <div className="form-field">
        <label className="checkbox-label">
          <input
            type="checkbox"
            disabled={!canEdit || blocker !== null}
            checked={draft.localPasswordDisabled}
            onChange={(e) => setDraft({ ...draft, localPasswordDisabled: e.target.checked })}
          />
          Require SSO (disable email and password sign-in)
        </label>
        {blocker && <p className="text-muted">{blocker}</p>}
        <p className="text-muted">
          When this is on, only members signing in with an enabled provider get in. Account admins always keep
          email and password access, so the organization cannot lock itself out. API tokens
          are not affected.
        </p>
      </div>
      <p className="text-muted">
        Two-factor authentication: Detect has no separate 2FA. Requiring Google or Microsoft SSO
        means the 2FA your identity provider enforces applies to every sign-in.
      </p>
      {canEdit ? (
        <button type="submit" className="btn btn-primary" disabled={saving || !dirty}>
          {saving ? 'Saving…' : 'Save'}
        </button>
      ) : (
        <p className="text-muted">Only an account admin can change the sign-in policy.</p>
      )}
    </form>
  );
}
